// Modal do nó Condição (mockup "Modal Condição A"): variável / regra / valor,
// chips de operador e caixas Verdadeiro/Falso mostrando o nó alvo de cada saída.
// Contrato: { open: "create"|"edit"|null, onSave, data, onUpdate, close };
// `edges`/`nodes` são opcionais (o editor passa para mostrar "→ nó alvo").
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";

import { i18n } from "../../translate/i18n";
import AerosDialog from "../AerosDialog";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { CONDITION_OPERATORS, operatorSymbol, nodeTitle } from "../../pages/FlowBuilderConfig/flowRules";

const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const borderInput = isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)";
  return {
    row: { display: "grid", gridTemplateColumns: "1.3fr 0.7fr 1.3fr", gap: 8, alignItems: "end" },
    mono: { "& .MuiOutlinedInput-input": { fontFamily: MONO, color: stateText(theme, AEROS.cyan, "info") } },
    rule: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${borderInput}`,
      borderRadius: 10,
      padding: "9px 12px",
      fontSize: 13,
      fontFamily: MONO,
      textAlign: "center",
      color: theme.palette.text.primary,
    },
    ops: { display: "flex", gap: 6, flexWrap: "wrap" },
    op: {
      fontFamily: MONO,
      fontSize: 11,
      fontWeight: 700,
      borderRadius: 6,
      padding: "3px 9px",
      cursor: "pointer",
      color: theme.palette.text.secondary,
      background: "transparent",
      border: `1px solid ${borderInput}`,
    },
    opOn: {
      color: stateText(theme, "#C08BFF", "purple"),
      backgroundColor: "rgba(160,68,255,0.15)",
      borderColor: "transparent",
    },
    outs: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 },
    trueText: { color: stateText(theme, AEROS.success, "success") },
    falseText: { color: stateText(theme, AEROS.error, "error") },
    outBox: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "9px 11px",
      borderRadius: 10,
      fontSize: 12,
      "& b": { fontWeight: 700, display: "block" },
      "& small": { fontSize: 10.5, color: theme.palette.text.secondary },
    },
    dot: { width: 8, height: 8, borderRadius: "50%", flex: "none" },
  };
});

const OUTS = [
  { handle: "true", labelKey: "aeros.flow.condition.true", color: AEROS.success, textClass: "trueText" },
  { handle: "false", labelKey: "aeros.flow.condition.false", color: AEROS.error, textClass: "falseText" },
];

const FlowBuilderConditionCompareModal = ({ open, onSave, data, onUpdate, close, edges = [], nodes = [] }) => {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const [leftValue, setLeftValue] = useState("");
  const [rightValue, setRightValue] = useState("");
  const [operator, setOperator] = useState("equals");

  const isUnary = !!CONDITION_OPERATORS.find((o) => o.value === operator)?.unary;

  useEffect(() => {
    if (open === "edit" && data) {
      setLeftValue(data.data?.leftValue || "");
      setRightValue(data.data?.rightValue || "");
      setOperator(data.data?.operator || "equals");
    } else {
      setLeftValue("");
      setRightValue("");
      setOperator("equals");
    }
  }, [open, data]);

  const targetOf = (handle) => {
    if (!data?.id) return null;
    const edge = edges.find((e) => e.source === data.id && (e.sourceHandle || "a") === handle);
    const node = edge && nodes.find((n) => n.id === edge.target);
    return node ? nodeTitle(node) : null;
  };

  const handleSave = () => {
    if (!leftValue.trim()) return toast.error(i18n.t("aeros.flow.condition.required"));
    const payload = { leftValue, operator, rightValue: isUnary ? "" : rightValue };
    if (open === "edit" && data) onUpdate({ ...data, data: { ...data.data, ...payload } });
    else onSave(payload);
    close(false);
  };

  return (
    <AerosDialog
      open={open === "create" || open === "edit"}
      onClose={() => close(false)}
      width={460}
      title={i18n.t(open === "edit" ? "aeros.flow.condition.title" : "aeros.flow.condition.addTitle")}
      onSubmit={handleSave}
      submitLabel={i18n.t(open === "edit" ? "aeros.flow.save" : "aeros.flow.add")}
    >
      <div className={classes.row}>
        <div>
          <label className={m.fieldLabel}>{i18n.t("aeros.flow.condition.variable")}</label>
          <TextField
            variant="outlined"
            className={`${m.input} ${classes.mono}`}
            value={leftValue}
            onChange={(e) => setLeftValue(e.target.value)}
            placeholder={i18n.t("aeros.flow.condition.variablePlaceholder")}
            autoFocus
          />
        </div>
        <div>
          <label className={m.fieldLabel}>{i18n.t("aeros.flow.condition.rule")}</label>
          <div className={classes.rule}>{operatorSymbol(operator)}</div>
        </div>
        <div>
          <label className={m.fieldLabel}>{i18n.t("aeros.flow.condition.value")}</label>
          <TextField
            variant="outlined"
            className={m.input}
            value={isUnary ? "" : rightValue}
            onChange={(e) => setRightValue(e.target.value)}
            placeholder={i18n.t("aeros.flow.condition.valuePlaceholder")}
            disabled={isUnary}
          />
        </div>
      </div>
      <div className={classes.ops}>
        {CONDITION_OPERATORS.map((o) => (
          <button
            key={o.value}
            type="button"
            className={`${classes.op} ${o.value === operator ? classes.opOn : ""}`}
            onClick={() => setOperator(o.value)}
          >
            {operatorSymbol(o.value)}
          </button>
        ))}
      </div>
      <div className={classes.outs}>
        {OUTS.map((o) => {
          const target = targetOf(o.handle);
          return (
            <div
              key={o.handle}
              className={classes.outBox}
              style={{ backgroundColor: `${o.color}14`, border: `1px solid ${o.color}4D` }}
            >
              <span className={classes.dot} style={{ background: o.color }} />
              <div>
                <b className={classes[o.textClass]}>{i18n.t(o.labelKey)}</b>
                <small>{target ? `→ ${target}` : i18n.t("aeros.flow.condition.noConnection")}</small>
              </div>
            </div>
          );
        })}
      </div>
    </AerosDialog>
  );
};

export default FlowBuilderConditionCompareModal;
